import {
  mdiBank,
  mdiChevronLeft,
  mdiChevronRight,
  mdiMapMarker,
} from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { CustomerDetailModel } from "../../../../service/models/orders/customer_detail_model";
import ModalDialog from "../../../../ui/new_components/common/modal";
import AddBillingWidget from "./AddBilling";
import {
  AddressType,
  CustomerBillingAddressModel,
  RegisteredofficeAddressModel,
} from "../../../../context_providers/auth/user_model";
import { GetBillingProfilesOfUserAndTypeApi } from "../../../../service/repos/customer/billing_profile_repo";

interface NewBillingProfileWidgetProps {
  registered_address?: RegisteredofficeAddressModel; // Consider specifying a proper type instead of `any`
  uid?: string;
  type: string;
  onDeliveryAddressAdded?: (address: CustomerBillingAddressModel) => void;
  onDeliveryAddressUpdated?: (address: CustomerBillingAddressModel) => void;
  setRegisteredAddress: (data: RegisteredofficeAddressModel) => void;
  viewOnly?: boolean;
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
}
const NewDeliveryWidget: FunctionComponent<NewBillingProfileWidgetProps> = ({
  registered_address,
  uid,
  type,
  onDeliveryAddressAdded,
  onDeliveryAddressUpdated,
  setRegisteredAddress,
  viewOnly = false,
  addOpen,
  onAddOpenChange,
}) => {
  const { showToast } = useToast();
  const register_address = registered_address;
  const [addNewInternal, setAddNewInternal] = useState(false);
  const isAddControlled = onAddOpenChange !== undefined;
  const addNew = isAddControlled ? !!addOpen : addNewInternal;
  const setAddNew = (v: boolean) =>
    isAddControlled ? onAddOpenChange!(v) : setAddNewInternal(v);
  const [edit, setEdit] = useState(false);
  const [editProfile, setEditProfile] = useState<CustomerDetailModel | null>(
    null,
  );
  const [customers, setCustomers] = useState<CustomerDetailModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (uid) {
      getCustomers();
    }
  }, [uid]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize(); // initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getCustomers = async () => {
    setLoading(true);
    const res = await GetBillingProfilesOfUserAndTypeApi(uid, type);

    if (res.success) {
      setCustomers(res.data.billing_profiles);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  const l = page * 2 - 1;
  const h = l + 2 - 1;

  const renderCardBody = (customer: CustomerDetailModel) => (
    <>
      <div className="flex gap-2 my-1">
        <span>
          <Icon path={mdiBank} className="h-4 w-4 text-gray-500"></Icon>
        </span>
        <p>
          <span className="font-semibold">Branch Name: </span>
          <span>{customer?.branch}</span>
        </p>
      </div>
      <hr className="my-2" />
      <div className="flex gap-1">
        <span className="font-semibold"> Country :</span>
        <p>{customer.address?.country ?? "--"}</p>
      </div>
      <div>
        <span className="font-semibold"> State : </span>
        <span>{customer.address?.state ?? "--"}</span>
      </div>
      <div>
        <span className="font-semibold"> City : </span>
        <span>{customer.address?.city ?? "--"}</span>
      </div>

      <hr className="my-2" />
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <Icon path={mdiMapMarker} className="h-4 w-4 text-gray-500" />
          <span className="font-semibold text-balck">Address</span>
        </div>
        <div className="text-gray-500 text-xs ">
          {customer.address.line1}, {customer.address.line2},{" "}
          {customer.address.line3},{customer.address.city},{" "}
          {customer.address.state}, {customer.address.country} -{" "}
          {customer.address.pincode}
        </div>
      </div>
    </>
  );

  return (
    <>
      {addNew && (
        <ModalDialog
          title="Add Delivery Address"
          show={addNew}
          onClose={() => setAddNew(false)}
        >
          <div className="p-2 md:p-5">
            <AddBillingWidget
              onAdded={(customerDetail) => {
                const isDuplicate = customers.some(
                  (customer) =>
                    (customer.branch ?? "").trim().toLowerCase() ===
                    (customerDetail?.branch ?? "").trim().toLowerCase()
                );

                if (isDuplicate) {
                  showToast({
                    type: ToastType.error,
                    text: "A delivery profile with this branch name already exists.",
                  });
                  return; // Stop execution if duplicate found
                } else {
                  showToast({
                    type: ToastType.success,
                    text: "Created Successfully!",
                  });
                }
                setAddNew(false); // Close the modal

                const billingProfile: CustomerBillingAddressModel = {
                  branch_name: customerDetail.branch,
                  type: AddressType.delivery,
                  line1: customerDetail.address.line1,
                  line2: customerDetail.address.line2 || "",
                  line3: customerDetail.address.line3 || "",
                  city: customerDetail.address.city,
                  state: customerDetail.address.state,
                  country: customerDetail.address.country,
                  pincode: customerDetail.address.pincode,
                };

                setCustomers((prev) => [...prev, customerDetail]);

                if (onDeliveryAddressAdded) {
                  onDeliveryAddressAdded(billingProfile);
                }

                setPage((prevPage) =>
                  Math.ceil((customers.length + 1) / (isMobile ? 1 : 2))
                );
              }}
              uid={uid}
              registered_address={register_address}
              setRegisteredAddress={setRegisteredAddress}
              type={AddressType.delivery}
            />
          </div>
        </ModalDialog>
      )}

      {editProfile && (
        <ModalDialog
          title="Edit Delivery Address"
          show={!!editProfile}
          onClose={() => setEditProfile(null)}
        >
          <div className="p-2 md:p-5">
            <AddBillingWidget
              key={editProfile.branch}
              editProfile={editProfile}
              onAdded={() => {}}
              onEdited={(customerDetail) => {
                setEditProfile(null);

                const billingProfile: CustomerBillingAddressModel = {
                  branch_name: customerDetail.branch,
                  type: AddressType.delivery,
                  line1: customerDetail.address.line1,
                  line2: customerDetail.address.line2 || "",
                  line3: customerDetail.address.line3 || "",
                  city: customerDetail.address.city,
                  state: customerDetail.address.state,
                  country: customerDetail.address.country,
                  pincode: customerDetail.address.pincode,
                };

                setCustomers((prev) =>
                  prev.map((c) =>
                    (c.branch ?? "").trim().toLowerCase() ===
                    (customerDetail.branch ?? "").trim().toLowerCase()
                      ? customerDetail
                      : c,
                  ),
                );

                if (onDeliveryAddressUpdated) {
                  onDeliveryAddressUpdated(billingProfile);
                }
              }}
              uid={uid}
              registered_address={register_address}
              setRegisteredAddress={setRegisteredAddress}
              type={AddressType.delivery}
            />
          </div>
        </ModalDialog>
      )}

      {!viewOnly && (
        <div className="hidden md:flex justify-end gap-2 items-end my-2 mt-5 ">
          <button
            onClick={() => {
              setAddNew(true);
            }}
            className="cursor-pointer text-white bg-myPrimaryColor px-3 py-1 rounded font-semibold"
          >
            Add new
          </button>
        </div>
      )}

      {viewOnly ? (
        <>
          {/* Mobile: horizontal swipe carousel */}
          <div className="md:hidden min-w-0">
            <div
              className="flex flex-nowrap gap-3 w-full max-w-full overflow-x-auto snap-x snap-mandatory pb-2 text-xs"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {customers.map((customer, i) => (
                <div
                  key={i}
                  style={{ flex: "0 0 85%" }}
                  className="snap-start bg-white rounded-lg border p-3 shadow-sm"
                >
                  {renderCardBody(customer)}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
            {customers.map((customer, i) => (
              <div
                key={i}
                className="bg-white h-60 w-full p-5 rounded-lg border text-xs"
              >
                {renderCardBody(customer)}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={`p-1 flex justify-center items-center bg-white shadow rounded-full ${
              page <= 1 && "invisible"
            }`}
          >
            <Icon
              path={mdiChevronLeft}
              className={`${isMobile ? "h-5 w-5" : "h-10 w-10"} text-gray-400`}
            />
          </button>

          <div
            className="rounded-md text-sm flex w-full"
            style={{ minHeight: "120px" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-5 w-full">
              {customers
                .filter((_, i) => {
                  if (isMobile) return i === page - 1;
                  return i >= l - 1 && i <= h - 1;
                })
                .map((customer, i) => (
                  <div
                    className={`relative cursor-pointer bg-white hover:bg-green-50 ${
                      isMobile ? "h-fit" : "h-60"
                    } w-full p-5 rounded-lg border`}
                    key={i}
                    onClick={() => {
                      setEdit(false);
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditProfile(customer);
                      }}
                      className="absolute top-2 right-2 text-xs text-myPrimaryColor font-semibold underline"
                    >
                      Edit
                    </button>
                    {renderCardBody(customer)}
                  </div>
                ))}
            </div>
          </div>

          <button
            onClick={() =>
              setPage((p) =>
                Math.min(
                  p + 1,
                  Math.ceil(customers.length / (isMobile ? 1 : 2)),
                ),
              )
            }
            className={`p-1 flex justify-center items-center bg-white shadow rounded-full ${
              page >= Math.ceil(customers.length / (isMobile ? 1 : 2))
                ? "invisible"
                : ""
            }`}
          >
            <Icon
              path={mdiChevronRight}
              className={`${isMobile ? "h-5 w-5" : "h-10 w-10"} text-gray-400`}
            />
          </button>
        </div>
      )}
    </>
  );
};

export default NewDeliveryWidget;
